import Controller from '@ember/controller'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { didCancel } from 'ember-concurrency'
import { tracked } from '@glimmer/tracking'
import { waitForPromise } from '@ember/test-waiters'
import { isAbortError } from 'teacher-ui-client/errors/http-error'
import type RouterService from '@ember/routing/router-service'
import type RolloverStudentImportService from 'teacher-ui-client/services/rollover-student-import'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import type SubscriptionUtilisationService from 'teacher-ui-client/services/subscription-utilisation'

export default class ConfirmController extends Controller {
  @service router!: RouterService

  @service rolloverStudentImport!: RolloverStudentImportService

  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service log!: Log

  @service subscriptionUtilisation!: SubscriptionUtilisationService

  @tracked
  importPromptVisible = false

  get importJob() {
    return this.rolloverStudentImport.importJob
  }

  get candidateStudents() {
    return this.importJob.candidateStudents ?? []
  }

  get duplicateStudentsInSource() {
    return this.candidateStudents.some((student) => student.duplicateSource)
  }

  get duplicateStudentsInDestination() {
    return this.candidateStudents.some((student) => student.duplicateDestination)
  }

  get studentsNameChanged() {
    return this.candidateStudents.some((student) => student.studentNameChange)
  }

  get displayWarning() {
    return this.duplicateStudentsInSource || this.duplicateStudentsInDestination || this.studentsNameChanged
  }

  get filename() {
    return this.importJob.file?.name
  }

  get studentsCount() {
    return this.candidateStudents.length
  }

  get isImporting() {
    return this.rolloverStudentImport.importCsv.isRunning
  }

  reloadSubscriptionUtilisationRecords() {
    return this.subscriptionUtilisation.fetchAll()
  }

  /**
   * Start an async process of uploading/importing students
   */
  async confirmImport() {
    try {
      void this.router.transitionTo('manage.rollover.steps.students.import.importing')
      const importResult = await waitForPromise(this.rolloverStudentImport.importCsv.perform())
      const { importJob } = this
      if (importJob.imported) {
        await this.reloadSubscriptionUtilisationRecords()
        this.flashQueue.clearMessages()
        this.flashQueue.addSuccess({
          subtitle: this.intl.t('manage.rollover.steps.students.importSuccessful', { htmlSafe: true }),
        })
        void this.router.replaceWith('manage.rollover.steps.students')
      } else {
        this.unexpectedErrorHandler(importResult, 'student import')
      }
    } catch (error) {
      // should only reject for task cancel or network failure
      if (!didCancel(error) && !isAbortError(error)) {
        this.unexpectedErrorHandler(error, 'student import')
      }
    }
  }

  @action
  showImportPrompt() {
    this.importPromptVisible = true
  }

  @action
  hideImportPrompt() {
    this.importPromptVisible = false
  }

  @action
  submit() {
    this.hideImportPrompt()
    void this.confirmImport()
  }

  @action
  cancel() {
    this.hideImportPrompt()
    this.rolloverStudentImport.clearJob()

    this.flashQueue.clearMessages()
    void this.router.replaceWith('manage.rollover.steps.students')
  }

  @action
  unexpectedErrorHandler(error: any, operation: string) {
    const detail = error?.detail || ''
    const { uuid } = this.log.error(`Unexpected error during ${operation}: ${detail}`, error)
    void this.router.replaceWith('manage.rollover.steps.students.import.error', uuid)
  }
}
